import React, { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Card,
  DatePicker,
  Empty,
  Input,
  message,
  Pagination,
  Result,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import {
  EyeOutlined,
  HistoryOutlined,
  ReloadOutlined,
  SearchOutlined,
  UserOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import { getApiErrorMessage } from '../utils/api'
import {
  listClassTicketIssuances,
  listClassTicketTemplates
} from '../api/classTicket'
import { formatTz, now as tzNow } from '../utils/timezone'
import ResponsiveSelect from '../components/common/ResponsiveSelect'

/**
 * 堂票發放紀錄
 *
 * 以「發放」為單位，依發放時間列出每一張堂票是怎麼發出去的：
 * 後台手動發放或結帳購買、銷售或贈送、金額與支付方式、銷售人員、經手人。
 * 上方統計卡顯示目前篩選區間的發放筆數、總堂數、銷售金額、贈送筆數。
 *
 * 和「堂票紀錄」的差別：那邊是看每張票「現在」剩幾堂、能不能用；
 * 這裡是回頭查「什麼時候、誰、發了什麼給誰」。
 */

const { Title, Text } = Typography
const { RangePicker } = DatePicker

const PageWrapper = styled.div`
  min-height: calc(100vh - 120px);
  padding: 24px;
  background: #f5f5f5;

  @media (max-width: 768px) {
    padding: 12px;
  }
`

const ContentWrapper = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const HeaderCard = styled(Card)`
  border-radius: 16px;
  border: 1px solid #eef0f5;
  box-shadow: 0 6px 18px rgba(15, 37, 98, 0.05);

  .ant-card-body {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
    justify-content: space-between;
  }

  @media (max-width: 768px) {
    .ant-card-body {
      flex-direction: column;
      align-items: stretch;
      text-align: center;
    }
    .ant-card-body > .ant-space {
      justify-content: center;
      width: 100%;
    }
  }
`

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }
`

const StatCard = styled.div`
  background: #fff;
  border: 1px solid #eef0f5;
  border-radius: 14px;
  padding: 14px 16px;
  box-shadow: 0 6px 18px rgba(15, 37, 98, 0.04);

  .label {
    font-size: 13px;
    color: #8b7d7d;
  }
  .value {
    margin-top: 4px;
    font-size: 22px;
    font-weight: 600;
    color: #2c2531;
    font-variant-numeric: tabular-nums;
  }
  .sub {
    margin-top: 2px;
    font-size: 12px;
    color: #a99c9c;
  }

  @media (max-width: 768px) {
    padding: 12px;
    .value {
      font-size: 18px;
    }
  }
`

const TableCard = styled(Card)`
  border-radius: 16px;
  border: 1px solid #eef0f5;
  box-shadow: 0 6px 18px rgba(15, 37, 98, 0.05);
`

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    > * {
      width: 100% !important;
    }
  }
`

const DesktopTable = styled(Table)`
  @media (max-width: 768px) {
    display: none;
  }
`

const MobileList = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
`

const MobileItemCard = styled.div`
  background: #fff;
  border: 1px solid #eef0f5;
  border-radius: 12px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const MobileTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
`

const MobileTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #2c2531;
`

const MobileMeta = styled.div`
  font-size: 12px;
  color: #8b7d7d;
  margin-top: 2px;
`

const MobileMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 13px;
  color: #6b7280;

  span b {
    color: #1d1d1f;
    font-weight: 600;
    margin-left: 2px;
  }
`

const MobileActionRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;

  .ant-btn {
    flex: 1;
  }
`

const MobilePagination = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    justify-content: center;
    margin-top: 16px;
  }
`

const SOURCE_META = {
  manual: { text: '後台發放', color: 'blue' },
  checkout: { text: '結帳購買', color: 'purple' },
  booking: { text: '預約贈送', color: 'cyan' },
  membership: { text: '會員贈送', color: 'gold' }
}

const ISSUE_TYPE_META = {
  sale: { text: '銷售', color: 'magenta' },
  gift: { text: '贈送', color: 'green' }
}

const PAYMENT_METHOD_TEXT = {
  cash: '現金',
  card: '刷卡',
  mobile_payment: '行動支付',
  transfer: '轉帳'
}

const STATUS_META = {
  active: { text: '可使用', color: 'green' },
  used_up: { text: '使用完畢', color: 'default' },
  expired: { text: '已過期', color: 'orange' },
  voided: { text: '已作廢', color: 'red' }
}

const money = (n) => `$${Number(n || 0).toLocaleString()}`

// 預設看本月
const defaultRange = () => [tzNow().startOf('month'), tzNow()]

const blankFilters = () => ({
  range: defaultRange(),
  keyword: '',
  templateId: '',
  source: '',
  issueType: '',
  salesPersonnel: ''
})

const ClassTicketIssuanceRecordsPage = () => {
  const { user } = useAuth()
  const { hasFeature, permissionsLoaded } = useMembership()
  const navigate = useNavigate()
  const businessId = user?.businessId
  const featureEnabled = hasFeature('classTicket')

  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [summary, setSummary] = useState({
    count: 0,
    totalSessions: 0,
    saleAmount: 0,
    giftCount: 0,
    voidedCount: 0
  })
  const [page, setPage] = useState(1)
  const pageSize = 20
  const [filters, setFilters] = useState(blankFilters)
  const [filterDraft, setFilterDraft] = useState(filters)
  const [templates, setTemplates] = useState([])

  const fetchList = useCallback(async () => {
    if (!businessId) return
    setLoading(true)
    try {
      const { range, ...rest } = filters
      // RangePicker 的值是使用者挑的「牆上日期」，直接取 YYYY-MM-DD 給後端用店家時區解讀
      const params = {
        page,
        pageSize,
        ...rest,
        startDate: range?.[0] ? range[0].format('YYYY-MM-DD') : '',
        endDate: range?.[1] ? range[1].format('YYYY-MM-DD') : ''
      }
      const res = await listClassTicketIssuances(businessId, params)
      const data = res?.data?.data || {}
      setItems(data.items || [])
      setTotal(data.total || 0)
      setSummary((s) => ({ ...s, ...(data.summary || {}) }))
    } catch (err) {
      message.error(getApiErrorMessage(err, '取得堂票發放紀錄失敗'))
    } finally {
      setLoading(false)
    }
  }, [businessId, page, filters])

  useEffect(() => {
    if (!featureEnabled || !businessId) return
    listClassTicketTemplates(businessId, { includeInactive: '1' })
      .then((res) => setTemplates(res?.data?.data || []))
      .catch((err) => console.warn('fetch templates failed', err))
  }, [businessId, featureEnabled])

  useEffect(() => {
    if (featureEnabled) fetchList()
  }, [fetchList, featureEnabled])

  const applyFilters = () => {
    setFilters(filterDraft)
    setPage(1)
  }
  const resetFilters = () => {
    const blank = blankFilters()
    setFilterDraft(blank)
    setFilters(blank)
    setPage(1)
  }

  const goTicket = (r) =>
    navigate(`/class-ticket/records?ticketCode=${encodeURIComponent(r.ticketCode)}`)

  if (!permissionsLoaded) {
    return (
      <PageWrapper>
        <Spin />
      </PageWrapper>
    )
  }
  if (!featureEnabled) {
    return (
      <PageWrapper>
        <Result
          status='warning'
          title='您的方案尚未開通堂票模組'
          subTitle='請升級至高階方案以使用堂票功能。'
        />
      </PageWrapper>
    )
  }

  const renderPrice = (r) => {
    if (r.issueType === 'gift') return <Text type='secondary'>贈送</Text>
    return (
      <div>
        <div style={{ fontVariantNumeric: 'tabular-nums' }}>
          {r.status === 'voided' ? <Text delete>{money(r.price)}</Text> : money(r.price)}
        </div>
        <Text type='secondary' style={{ fontSize: 12 }}>
          {PAYMENT_METHOD_TEXT[r.paymentMethod] || '現金'}
        </Text>
      </div>
    )
  }

  const columns = [
    {
      title: '發放時間',
      dataIndex: 'issuedAt',
      key: 'issuedAt',
      width: 150,
      render: (v, r) => formatTz(v || r.createdAt, 'YYYY/MM/DD HH:mm', '—')
    },
    {
      title: '會員',
      dataIndex: 'customerName',
      key: 'customerName',
      width: 150,
      render: (v, r) => (
        <div>
          <div>{v || '—'}</div>
          {r.customerPhone && (
            <Text type='secondary' style={{ fontSize: 12 }}>
              {r.customerPhone}
            </Text>
          )}
        </div>
      )
    },
    {
      title: '堂票',
      dataIndex: 'templateName',
      key: 'templateName',
      render: (v, r) => (
        <div>
          <div>{v}</div>
          <Text code style={{ fontSize: 12 }}>
            {r.ticketCode}
          </Text>
        </div>
      )
    },
    {
      title: '堂數',
      dataIndex: 'totalCount',
      key: 'totalCount',
      width: 70,
      align: 'right'
    },
    {
      title: '性質',
      dataIndex: 'issueType',
      key: 'issueType',
      width: 80,
      render: (v) => {
        const meta = ISSUE_TYPE_META[v] || ISSUE_TYPE_META.sale
        return <Tag color={meta.color}>{meta.text}</Tag>
      }
    },
    {
      title: '來源',
      dataIndex: 'source',
      key: 'source',
      width: 130,
      render: (v, r) => {
        const meta = SOURCE_META[v] || { text: v || '—', color: 'default' }
        return (
          <div>
            <Tag color={meta.color}>{meta.text}</Tag>
            {r.sourceOrderNo && (
              <div>
                <Text type='secondary' style={{ fontSize: 12 }}>
                  訂單 {r.sourceOrderNo}
                </Text>
              </div>
            )}
          </div>
        )
      }
    },
    {
      title: '金額 / 支付',
      key: 'price',
      width: 110,
      align: 'right',
      render: (_, r) => renderPrice(r)
    },
    {
      title: '銷售人員',
      dataIndex: 'salesPersonnel',
      key: 'salesPersonnel',
      width: 100,
      render: (v) => v || '—'
    },
    {
      title: '經手人',
      dataIndex: 'operatorName',
      key: 'operatorName',
      width: 100,
      render: (v) => v || '—'
    },
    {
      title: '目前狀態',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (v) => {
        const meta = STATUS_META[v] || { text: v, color: 'default' }
        return <Tag color={meta.color}>{meta.text}</Tag>
      }
    },
    {
      title: '備註',
      dataIndex: 'note',
      key: 'note',
      width: 160,
      ellipsis: { showTitle: false },
      render: (v) =>
        v ? (
          <Tooltip title={v}>
            <span>{v}</span>
          </Tooltip>
        ) : (
          '—'
        )
    },
    {
      title: '操作',
      key: 'actions',
      width: 90,
      fixed: 'right',
      align: 'center',
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title='查看這張堂票'>
            <Button
              type='text'
              size='small'
              icon={<EyeOutlined />}
              onClick={() => goTicket(r)}
            />
          </Tooltip>
          <Tooltip title='前往會員'>
            <Button
              type='text'
              size='small'
              icon={<UserOutlined />}
              disabled={!r.customerId}
              onClick={() => navigate(`/customers/${r.customerId}`)}
            />
          </Tooltip>
        </Space>
      )
    }
  ]

  return (
    <PageWrapper>
      <ContentWrapper>
        <HeaderCard>
          <Space size='middle' align='center'>
            <HistoryOutlined style={{ fontSize: 22, color: '#d5728a' }} />
            <Title level={4} style={{ margin: 0 }}>
              堂票發放紀錄
            </Title>
          </Space>
          <Space wrap>
            <Button icon={<ReloadOutlined />} onClick={() => fetchList()}>
              重新整理
            </Button>
          </Space>
        </HeaderCard>

        <StatGrid>
          <StatCard>
            <div className='label'>發放筆數</div>
            <div className='value'>{summary.count}</div>
            {summary.voidedCount > 0 && (
              <div className='sub'>含已作廢 {summary.voidedCount} 筆</div>
            )}
          </StatCard>
          <StatCard>
            <div className='label'>發放總堂數</div>
            <div className='value'>{summary.totalSessions}</div>
          </StatCard>
          <StatCard>
            <div className='label'>銷售金額</div>
            <div className='value'>{money(summary.saleAmount)}</div>
            <div className='sub'>不含贈送與已作廢</div>
          </StatCard>
          <StatCard>
            <div className='label'>贈送筆數</div>
            <div className='value'>{summary.giftCount}</div>
          </StatCard>
        </StatGrid>

        <TableCard>
          <FilterBar>
            <RangePicker
              value={filterDraft.range}
              onChange={(v) => setFilterDraft((d) => ({ ...d, range: v }))}
              format='YYYY-MM-DD'
              allowClear
              inputReadOnly
              style={{ width: 260 }}
            />
            <Input
              allowClear
              placeholder='會員姓名 / 手機 / 堂票編號'
              prefix={<SearchOutlined />}
              style={{ width: 240 }}
              value={filterDraft.keyword}
              onChange={(e) =>
                setFilterDraft((d) => ({ ...d, keyword: e.target.value }))
              }
              onPressEnter={applyFilters}
            />
            <ResponsiveSelect
              allowClear
              placeholder='堂票名稱'
              style={{ width: 200 }}
              showSearch
              optionFilterProp='label'
              value={filterDraft.templateId || undefined}
              options={templates.map((t) => ({ label: t.name, value: t._id }))}
              onChange={(v) =>
                setFilterDraft((d) => ({ ...d, templateId: v || '' }))
              }
            />
            <ResponsiveSelect
              style={{ width: 130 }}
              value={filterDraft.source}
              options={[
                { label: '全部來源', value: '' },
                { label: '後台發放', value: 'manual' },
                { label: '結帳購買', value: 'checkout' }
              ]}
              onChange={(v) =>
                setFilterDraft((d) => ({ ...d, source: v || '' }))
              }
            />
            <ResponsiveSelect
              style={{ width: 120 }}
              value={filterDraft.issueType}
              options={[
                { label: '全部性質', value: '' },
                { label: '銷售', value: 'sale' },
                { label: '贈送', value: 'gift' }
              ]}
              onChange={(v) =>
                setFilterDraft((d) => ({ ...d, issueType: v || '' }))
              }
            />
            <Input
              allowClear
              placeholder='銷售人員'
              style={{ width: 140 }}
              value={filterDraft.salesPersonnel}
              onChange={(e) =>
                setFilterDraft((d) => ({
                  ...d,
                  salesPersonnel: e.target.value
                }))
              }
              onPressEnter={applyFilters}
            />
            <Button type='primary' onClick={applyFilters}>
              查詢
            </Button>
            <Button onClick={resetFilters}>重置</Button>
          </FilterBar>

          <DesktopTable
            rowKey='_id'
            columns={columns}
            dataSource={items}
            loading={loading}
            scroll={{ x: 1400 }}
            locale={{ emptyText: <Empty description='這段期間沒有發放紀錄' /> }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: false,
              showTotal: (t) => `共 ${t} 筆`,
              onChange: (p) => setPage(p)
            }}
          />

          <MobileList>
            {loading ? (
              <Spin />
            ) : items.length === 0 ? (
              <Empty description='這段期間沒有發放紀錄' />
            ) : (
              items.map((r) => {
                const typeMeta = ISSUE_TYPE_META[r.issueType] || ISSUE_TYPE_META.sale
                const srcMeta = SOURCE_META[r.source] || {
                  text: r.source || '—',
                  color: 'default'
                }
                return (
                  <MobileItemCard key={r._id}>
                    <MobileTopRow>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <MobileTitle>{r.templateName}</MobileTitle>
                        <MobileMeta>
                          {r.customerName || '—'}
                          {r.customerPhone ? ` · ${r.customerPhone}` : ''}
                        </MobileMeta>
                        <MobileMeta>
                          {formatTz(r.issuedAt || r.createdAt, 'YYYY/MM/DD HH:mm', '—')}
                          {' · '}編號 {r.ticketCode}
                        </MobileMeta>
                      </div>
                      <Space size={4} wrap style={{ justifyContent: 'flex-end' }}>
                        <Tag color={typeMeta.color}>{typeMeta.text}</Tag>
                        <Tag color={srcMeta.color}>{srcMeta.text}</Tag>
                      </Space>
                    </MobileTopRow>
                    <MobileMetaRow>
                      <span>
                        堂數<b>{r.totalCount}</b>
                      </span>
                      {r.issueType !== 'gift' && (
                        <span>
                          金額<b>{money(r.price)}</b>
                          {' '}({PAYMENT_METHOD_TEXT[r.paymentMethod] || '現金'})
                        </span>
                      )}
                      {r.salesPersonnel && (
                        <span>
                          銷售<b>{r.salesPersonnel}</b>
                        </span>
                      )}
                      {r.operatorName && (
                        <span>
                          經手<b>{r.operatorName}</b>
                        </span>
                      )}
                      {r.status === 'voided' && <Tag color='red'>已作廢</Tag>}
                    </MobileMetaRow>
                    <MobileActionRow>
                      <Button icon={<EyeOutlined />} onClick={() => goTicket(r)}>
                        查看堂票
                      </Button>
                      <Button
                        icon={<UserOutlined />}
                        disabled={!r.customerId}
                        onClick={() => navigate(`/customers/${r.customerId}`)}
                      >
                        會員
                      </Button>
                    </MobileActionRow>
                  </MobileItemCard>
                )
              })
            )}
          </MobileList>

          {total > pageSize && (
            <MobilePagination>
              <Pagination
                current={page}
                pageSize={pageSize}
                total={total}
                showSizeChanger={false}
                size='small'
                onChange={(p) => setPage(p)}
              />
            </MobilePagination>
          )}
        </TableCard>
      </ContentWrapper>
    </PageWrapper>
  )
}

export default ClassTicketIssuanceRecordsPage
